import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  CircleF,
  GoogleMap,
  MarkerF,
  useJsApiLoader,
} from "@react-google-maps/api";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import AddLocationAltRoundedIcon from "@mui/icons-material/AddLocationAltRounded";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import FitScreenRoundedIcon from "@mui/icons-material/FitScreenRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RadarRoundedIcon from "@mui/icons-material/RadarRounded";
import { Card, PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, Spinner } from "../../components/UI/States";
import { Segmented } from "../../components/Fleet/FleetParts";
import { mapsUrl } from "../../components/Fleet/fleetFormat";
import { useIsDarkMode } from "../../theme/ThemeContext";
import {
  MAP_STYLE_DARK,
  MAP_STYLE_LIGHT,
  PLACES_LOADER_OPTIONS,
} from "../../utils/googleMaps";

// Plot GPS readings as accuracy circles: "42.614689, -71.324092 ±80 m" means
// the device was somewhere inside that circle. Replaces the standalone
// Leaflet "Geolocation Circle Plotter" page (v1.3). Circles live in this
// browser's localStorage only — nothing is sent to the server.

const STORAGE_KEY = "geomapping.circles";
const MAP_TYPE_KEY = "geomapping.mapType";
const FEET_PER_METER = 3.28084;
const METERS_PER_DEGREE = 111320;
// Satellite imagery runs out past ~19 in most places; framing a 5 m circle
// shouldn't zoom into "no imagery here".
const MAX_FIT_ZOOM = 19;
// Michiana — roughly the middle of the branch network.
const DEFAULT_CENTER = { lat: 41.7534, lng: -86.1108 };

// Bright enough to read on satellite imagery as well as the street map.
const SWATCHES = [
  "#22c55e",
  "#3b82f6",
  "#ef4444",
  "#f59e0b",
  "#a855f7",
  "#ec4899",
  "#06b6d4",
  "#facc15",
];

// Only what the search needs — Places bills by the fields requested.
const PLACE_FIELDS = ["geometry", "formatted_address", "name"];

// Enter on a highlighted suggestion belongs to Google's Autocomplete; it
// shouldn't also submit the search form and geocode the half-typed text.
const suggestionHighlighted = () =>
  Array.from(document.querySelectorAll(".pac-container")).some(
    (el) => el.offsetParent !== null && el.querySelector(".pac-item-selected"),
  );

const MAP_TYPES = [
  { value: "roadmap", label: "Map" },
  { value: "satellite", label: "Satellite" },
  { value: "hybrid", label: "Hybrid" },
];

// The shared basemap hides businesses and transit; here they're how you tell
// which building a reading landed on, so keep them.
const showsPlaces = (rule) =>
  rule.featureType !== "poi" && rule.featureType !== "transit";
const STYLE_LIGHT = MAP_STYLE_LIGHT.filter(showsPlaces);
const STYLE_DARK = MAP_STYLE_DARK.filter(showsPlaces);

const FIELD_CLASS = "app-input w-full rounded-xl border px-3 py-2 text-sm";

const validLat = (n) => Number.isFinite(n) && n >= -90 && n <= 90;
const validLng = (n) => Number.isFinite(n) && n >= -180 && n <= 180;
const round6 = (n) => Math.round(n * 1e6) / 1e6;
const newId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const nextSwatch = (color) =>
  SWATCHES[(SWATCHES.indexOf(color) + 1) % SWATCHES.length];

// "42.61, -71.32", "42.61 -71.32", and Google's "(42.61, -71.32)".
const COORD_PAIR =
  /^\s*\(?\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*\)?\s*$/;
const parseCoordPair = (text) => {
  const match = COORD_PAIR.exec(text || "");
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  return validLat(lat) && validLng(lng) ? { lat, lng } : null;
};

// Required number fields: "" must not read as 0.
const parseField = (text) => (String(text).trim() === "" ? NaN : Number(text));

const formatCoords = ({ lat, lng }) => `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

const formatRadius = (meters) => {
  const metric =
    meters >= 1000
      ? `${(meters / 1000).toFixed(1)} km`
      : `${Math.round(meters)} m`;
  const feet = meters * FEET_PER_METER;
  const imperial =
    feet >= 5280
      ? `${(feet / 5280).toFixed(1)} mi`
      : `${Math.round(feet).toLocaleString()} ft`;
  return `${metric} · ${imperial}`;
};

const readStorage = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / storage full — the page still works, it just forgets.
  }
};

const loadCircles = () => {
  const saved = readStorage(STORAGE_KEY, []);
  return Array.isArray(saved)
    ? saved.filter(
        (c) =>
          c &&
          c.id &&
          validLat(c.lat) &&
          validLng(c.lng) &&
          Number.isFinite(c.radius) &&
          c.radius >= 0,
      )
    : [];
};

const loadMapType = () => {
  const saved = readStorage(MAP_TYPE_KEY, "hybrid");
  return MAP_TYPES.some((t) => t.value === saved) ? saved : "hybrid";
};

// Bounds of a circle without the geometry library (not in the shared loader
// options). Plenty accurate for framing the view.
const circleBounds = ({ lat, lng }, radius) => {
  const dLat = radius / METERS_PER_DEGREE;
  const dLng =
    radius /
    (METERS_PER_DEGREE * Math.max(Math.cos((lat * Math.PI) / 180), 0.01));
  return new window.google.maps.LatLngBounds(
    { lat: Math.max(lat - dLat, -85), lng: lng - dLng },
    { lat: Math.min(lat + dLat, 85), lng: lng + dLng },
  );
};

const fitWithCap = (map, bounds) => {
  if (!map || !bounds) return;
  map.fitBounds(bounds, 64);
  window.google.maps.event.addListenerOnce(map, "idle", () => {
    if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
  });
};

const dotIcon = (color, selected) => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  fillColor: color,
  fillOpacity: 1,
  strokeColor: "#ffffff",
  strokeWeight: 2,
  scale: selected ? 7 : 5,
});

// One saved circle + its center dot. CircleF re-applies `center`/`options`
// whenever their identity changes, so both are memoized — re-setting
// `editable` on every render would drop the resize handles mid-drag.
const SavedCircle = ({ circle, selected, circleRefs, onSelect, onMapEdit }) => {
  const { id, lat, lng, radius, color, label } = circle;
  const center = useMemo(() => ({ lat, lng }), [lat, lng]);
  const options = useMemo(
    () => ({
      strokeColor: color,
      strokeOpacity: 0.95,
      strokeWeight: selected ? 3 : 2,
      fillColor: color,
      fillOpacity: selected ? 0.3 : 0.18,
      // Unselected circles let clicks through so you can pick a point inside
      // one; select via its center dot or the list.
      clickable: selected,
      draggable: selected,
      editable: selected,
      zIndex: selected ? 10 : 1,
    }),
    [color, selected],
  );
  const icon = useMemo(() => dotIcon(color, selected), [color, selected]);

  return (
    <>
      <CircleF
        center={center}
        radius={radius}
        options={options}
        onLoad={(inst) => {
          circleRefs.current[id] = inst;
        }}
        onUnmount={() => {
          delete circleRefs.current[id];
        }}
        onCenterChanged={() => selected && onMapEdit(id)}
        onRadiusChanged={() => selected && onMapEdit(id)}
      />
      <MarkerF
        position={center}
        title={`${label || "Untitled"} — ±${formatRadius(radius)}`}
        icon={icon}
        zIndex={selected ? 1000 : 100}
        onClick={() => onSelect(id)}
      />
    </>
  );
};

/* ------------------------------------------------------------------ */

const Field = ({ label, hint, error, children }) => (
  <label className="block min-w-0">
    <span className="mb-1 flex items-baseline justify-between gap-2 text-xs font-semibold">
      <span>{label}</span>
      {hint && <span className="app-muted truncate font-normal">{hint}</span>}
    </span>
    {children}
    {error && (
      <span className="mt-1 block text-xs text-rose-600 dark:text-rose-400">
        {error}
      </span>
    )}
  </label>
);

const SWATCH_CLASS =
  "h-7 w-7 rounded-full border-2 border-white/80 shadow-sm transition hover:scale-110";
const SWATCH_ON = "ring-2 ring-offset-2 ring-offset-transparent";

const ColorPicker = ({ value, onChange }) => {
  const custom = !SWATCHES.includes(value);
  return (
    <div
      className="flex flex-wrap items-center gap-2.5"
      role="radiogroup"
      aria-label="Circle color"
    >
      {SWATCHES.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value === color}
          aria-label={color}
          onClick={() => onChange(color)}
          className={`${SWATCH_CLASS} ${value === color ? SWATCH_ON : ""}`}
          style={{ backgroundColor: color, "--tw-ring-color": color }}
        />
      ))}
      <label
        title="Custom color"
        className={`relative cursor-pointer overflow-hidden ${SWATCH_CLASS} ${custom ? SWATCH_ON : ""}`}
        style={{
          "--tw-ring-color": value,
          background: custom
            ? value
            : "conic-gradient(#ef4444, #f59e0b, #facc15, #22c55e, #06b6d4, #3b82f6, #a855f7, #ec4899, #ef4444)",
        }}
      >
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-label="Custom color"
        />
      </label>
    </div>
  );
};

/* ------------------------------------------------------------------ */

const PanelHeading = ({ title, hint }) => (
  <div className="mb-4">
    <h2 className="text-base font-semibold tracking-tight">{title}</h2>
    {hint && <p className="app-muted mt-0.5 text-xs leading-relaxed">{hint}</p>}
  </div>
);

// Edits one saved circle in place (takes over the panel's form slot while a
// circle is selected). Text stays local so half-typed values ("-", "42.") don't fight
// the map; valid values write straight through.
const CircleEditor = ({ circle, onChange, onDelete, onDone }) => {
  const [lat, setLat] = useState(String(circle.lat));
  const [lng, setLng] = useState(String(circle.lng));
  const [radius, setRadius] = useState(String(circle.radius));
  const [copied, setCopied] = useState(false);

  // Dragging or resizing on the map changes the circle underneath us.
  useEffect(() => {
    setLat((s) => (parseField(s) === circle.lat ? s : String(circle.lat)));
  }, [circle.lat]);
  useEffect(() => {
    setLng((s) => (parseField(s) === circle.lng ? s : String(circle.lng)));
  }, [circle.lng]);
  useEffect(() => {
    setRadius((s) =>
      parseField(s) === circle.radius ? s : String(circle.radius),
    );
  }, [circle.radius]);

  const latError = !validLat(parseField(lat))
    ? "Must be between -90 and 90"
    : "";
  const lngError = !validLng(parseField(lng))
    ? "Must be between -180 and 180"
    : "";
  const radiusValue = parseField(radius);
  const radiusError = !(radiusValue >= 0) ? "Enter 0 or more" : "";

  const copy = () => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(formatCoords(circle)).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  return (
    <>
      <PanelHeading
        title={
          <span className="flex items-center gap-2">
            <span
              className="h-3.5 w-3.5 rounded-full border-2 border-white shadow ring-1 ring-black/10"
              style={{ backgroundColor: circle.color }}
            />
            Edit circle
          </span>
        }
        hint="Drag the circle on the map to move it, or drag its edge handles to resize it."
      />
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude" error={latError}>
            <input
              className={`${FIELD_CLASS} tabular-nums`}
              value={lat}
              onChange={(e) => {
                setLat(e.target.value);
                const n = parseField(e.target.value);
                if (validLat(n)) onChange({ lat: round6(n) });
              }}
            />
          </Field>
          <Field label="Longitude" error={lngError}>
            <input
              className={`${FIELD_CLASS} tabular-nums`}
              value={lng}
              onChange={(e) => {
                setLng(e.target.value);
                const n = parseField(e.target.value);
                if (validLng(n)) onChange({ lng: round6(n) });
              }}
            />
          </Field>
        </div>
        <Field
          label="Radius / accuracy (m)"
          hint={radiusError ? "" : formatRadius(radiusValue)}
          error={radiusError}
        >
          <input
            className={`${FIELD_CLASS} tabular-nums`}
            inputMode="decimal"
            value={radius}
            onChange={(e) => {
              setRadius(e.target.value);
              const n = parseField(e.target.value);
              if (n >= 0) onChange({ radius: n });
            }}
          />
        </Field>
        <Field label="Label">
          <input
            className={FIELD_CLASS}
            value={circle.label}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </Field>
        <Field label="Color">
          <ColorPicker
            value={circle.color}
            onChange={(color) => onChange({ color })}
          />
        </Field>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-2">
        <button
          type="button"
          className="app-btn app-btn-secondary app-btn-sm"
          onClick={copy}
        >
          {copied ? (
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          ) : (
            <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
          )}
          {copied ? "Copied" : "Copy"}
        </button>
        <a
          className="app-btn app-btn-secondary app-btn-sm"
          href={mapsUrl(circle.lat, circle.lng)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
          Google Maps
        </a>
        <button
          type="button"
          className="app-btn app-btn-secondary app-btn-sm text-rose-600 dark:text-rose-400"
          onClick={onDelete}
        >
          <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
          Delete
        </button>
        <button
          type="button"
          className="app-btn app-btn-primary app-btn-sm"
          onClick={onDone}
        >
          <CheckRoundedIcon sx={{ fontSize: 16 }} />
          Done
        </button>
      </div>
    </>
  );
};

const CircleRow = ({ circle, selected, onSelect, onFocus, onDelete }) => {
  const rowRef = useRef(null);

  // Picking a circle on the map scrolls its row into view inside the list
  // only — scrollIntoView would also yank the page up away from the map.
  useEffect(() => {
    const row = rowRef.current;
    const box = row && row.parentElement;
    if (!selected || !box) return;
    if (row.offsetTop < box.scrollTop) {
      box.scrollTop = row.offsetTop;
    } else if (
      row.offsetTop + row.offsetHeight >
      box.scrollTop + box.clientHeight
    ) {
      box.scrollTop = row.offsetTop + row.offsetHeight - box.clientHeight;
    }
  }, [selected]);

  return (
    <li
      ref={rowRef}
      className={`app-divider flex items-center gap-1 border-b py-1.5 pl-5 pr-3 last:border-b-0 ${
        selected ? "bg-[color:var(--app-hover)]" : ""
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="flex min-w-0 flex-1 items-center gap-3 py-1 text-left"
      >
        <span
          className="h-4 w-4 shrink-0 rounded-full border-2 border-white shadow ring-1 ring-black/10"
          style={{ backgroundColor: circle.color }}
        />
        <span className="min-w-0">
          <span
            className={`block truncate text-sm font-semibold ${circle.label ? "" : "app-muted italic"}`}
          >
            {circle.label || "Untitled"}
          </span>
          <span className="app-muted block truncate text-xs tabular-nums">
            {formatCoords(circle)} · ±{formatRadius(circle.radius)}
          </span>
        </span>
      </button>
      <button
        type="button"
        className="app-icon-btn h-8 w-8 shrink-0"
        title="Zoom to circle"
        aria-label="Zoom to circle"
        onClick={onFocus}
      >
        <CenterFocusStrongRoundedIcon fontSize="small" />
      </button>
      <button
        type="button"
        className="app-icon-btn h-8 w-8 shrink-0 hover:text-rose-600 dark:hover:text-rose-400"
        title="Delete circle"
        aria-label="Delete circle"
        onClick={onDelete}
      >
        <DeleteOutlineRoundedIcon fontSize="small" />
      </button>
    </li>
  );
};

/* ------------------------------------------------------------------ */

const Geomapping = () => {
  // The Places loader (not the shared map one): its key has address
  // autocomplete and geocoding enabled.
  const { isLoaded, loadError } = useJsApiLoader(PLACES_LOADER_OPTIONS);
  const isDark = useIsDarkMode();
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);
  const circleRefs = useRef({});

  const [circles, setCircles] = useState(loadCircles);
  const [selectedId, setSelectedId] = useState(null);
  const [mapType, setMapType] = useState(loadMapType);
  const [draft, setDraft] = useState({
    lat: "",
    lng: "",
    radius: "50",
    label: "",
    color: SWATCHES[0],
  });
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState("");
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => writeStorage(STORAGE_KEY, circles), [circles]);
  useEffect(() => writeStorage(MAP_TYPE_KEY, mapType), [mapType]);

  const draftLat = parseField(draft.lat);
  const draftLng = parseField(draft.lng);
  const draftRadius = parseField(draft.radius);
  const radiusValid = draftRadius >= 0;
  const draftPoint = useMemo(
    () =>
      validLat(draftLat) && validLng(draftLng)
        ? { lat: draftLat, lng: draftLng }
        : null,
    [draftLat, draftLng],
  );
  const latError =
    draft.lat.trim() && !validLat(draftLat) ? "Must be between -90 and 90" : "";
  const lngError =
    draft.lng.trim() && !validLng(draftLng)
      ? "Must be between -180 and 180"
      : "";
  const radiusError =
    draft.radius.trim() && !radiusValid ? "Enter 0 or more" : "";

  const selected = circles.find((c) => c.id === selectedId) || null;

  /* ---- map framing ---- */

  const focusOn = useCallback((point, radius) => {
    if (!mapRef.current || !window.google) return;
    fitWithCap(mapRef.current, circleBounds(point, radius > 0 ? radius : 0));
  }, []);

  const fitAll = useCallback(() => {
    if (!mapRef.current || !window.google || !circles.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    circles.forEach((c) => bounds.union(circleBounds(c, c.radius)));
    fitWithCap(mapRef.current, bounds);
  }, [circles]);

  /* ---- new circle ---- */

  const pickPoint = useCallback((point, label) => {
    setSelectedId(null);
    setDraft((d) => ({
      ...d,
      lat: String(round6(point.lat)),
      lng: String(round6(point.lng)),
      label: label && !d.label.trim() ? label : d.label,
    }));
  }, []);

  const addCircle = (e) => {
    e.preventDefault();
    if (!draftPoint || !radiusValid) return;
    const circle = {
      id: newId(),
      lat: round6(draftPoint.lat),
      lng: round6(draftPoint.lng),
      radius: draftRadius,
      label: draft.label.trim(),
      color: draft.color,
      createdAt: new Date().toISOString(),
    };
    setCircles((prev) => [circle, ...prev]);
    // Keep the radius (readings often share an accuracy) but move to the next
    // color so back-to-back circles are easy to tell apart.
    setDraft((d) => ({
      ...d,
      lat: "",
      lng: "",
      label: "",
      color: nextSwatch(d.color),
    }));
    focusOn(circle, circle.radius);
  };

  // Pasting "lat, lng" into either box fills both.
  const onCoordPaste = (e) => {
    const pair = parseCoordPair(e.clipboardData.getData("text"));
    if (!pair) return;
    e.preventDefault();
    pickPoint(pair);
    focusOn(pair, radiusValid ? draftRadius : 0);
  };

  const locateMe = () => {
    setLocateError("");
    if (!navigator.geolocation) {
      setLocateError("This browser can't share its location.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const { latitude, longitude, accuracy } = pos.coords;
        setSelectedId(null);
        setDraft((d) => ({
          ...d,
          lat: String(round6(latitude)),
          lng: String(round6(longitude)),
          radius: String(Math.round(accuracy)),
          label: d.label.trim() ? d.label : "My location",
        }));
        focusOn({ lat: latitude, lng: longitude }, accuracy);
      },
      (err) => {
        setLocating(false);
        setLocateError(
          err.code === 1
            ? "Location access is blocked for this site. Allow it in your browser's site settings, then try again."
            : "Couldn't get your location. Try again in a moment.",
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  // A suggestion was picked from the dropdown. Typing and pressing Enter
  // without picking one comes back with no geometry — the form's submit
  // (runSearch) geocodes that text instead.
  const onPlaceChanged = () => {
    const place = autocompleteRef.current && autocompleteRef.current.getPlace();
    const loc = place && place.geometry && place.geometry.location;
    if (!loc) return;
    const point = { lat: loc.lat(), lng: loc.lng() };
    // Google writes the chosen suggestion into the input; keep React in step.
    setQuery(
      searchInputRef.current
        ? searchInputRef.current.value
        : place.formatted_address || "",
    );
    setSearchError("");
    pickPoint(point, place.formatted_address || place.name);
    fitWithCap(
      mapRef.current,
      place.geometry.viewport || circleBounds(point, 0),
    );
  };

  const runSearch = (e) => {
    e.preventDefault();
    const text = query.trim();
    if (!text || !window.google) return;
    setSearchError("");

    const pair = parseCoordPair(text);
    if (pair) {
      pickPoint(pair);
      focusOn(pair, radiusValid ? draftRadius : 0);
      return;
    }

    setSearching(true);
    new window.google.maps.Geocoder()
      .geocode({ address: text })
      .then(({ results }) => {
        const top = results && results[0];
        if (!top) {
          setSearchError("No match for that address.");
          return;
        }
        const loc = top.geometry.location;
        pickPoint({ lat: loc.lat(), lng: loc.lng() }, top.formatted_address);
        fitWithCap(
          mapRef.current,
          top.geometry.viewport ||
            circleBounds({ lat: loc.lat(), lng: loc.lng() }, 0),
        );
      })
      .catch((err) => {
        setSearchError(
          err?.code === "ZERO_RESULTS"
            ? "No match for that address."
            : "Address search isn't available right now — paste coordinates instead.",
        );
      })
      .finally(() => setSearching(false));
  };

  /* ---- saved circles ---- */

  const updateCircle = useCallback((id, patch) => {
    setCircles((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    );
  }, []);

  const removeCircle = (id) => {
    setCircles((prev) => prev.filter((c) => c.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  };

  const clearAll = () => {
    setCircles([]);
    setSelectedId(null);
    setConfirmClear(false);
  };

  // The selected circle is draggable/editable on the map; read its new
  // center/radius back into state. The tolerance stops the echo when state
  // pushes the (rounded) value back into the circle.
  const syncFromMap = useCallback((id) => {
    const inst = circleRefs.current[id];
    const center = inst && inst.getCenter();
    const radius = inst && inst.getRadius();
    if (!center || !Number.isFinite(radius)) return;
    const lat = center.lat();
    const lng = center.lng();
    setCircles((prev) => {
      let changed = false;
      const next = prev.map((c) => {
        if (c.id !== id) return c;
        if (
          Math.abs(c.lat - lat) < 1e-7 &&
          Math.abs(c.lng - lng) < 1e-7 &&
          Math.abs(c.radius - radius) < 0.05
        ) {
          return c;
        }
        changed = true;
        return {
          ...c,
          lat: round6(lat),
          lng: round6(lng),
          radius: Math.round(radius * 10) / 10,
        };
      });
      return changed ? next : prev;
    });
  }, []);

  /* ---- map ---- */

  const mapOptions = useMemo(
    () =>
      isLoaded
        ? {
            mapTypeId: mapType,
            // Custom styles are for the street map only. On Hybrid, Google
            // draws its label layer with them, and the theme's land/water
            // colors paint over the satellite imagery.
            styles:
              mapType === "roadmap" ? (isDark ? STYLE_DARK : STYLE_LIGHT) : [],
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            scaleControl: true,
            gestureHandling: "greedy",
            draggableCursor: "crosshair",
            zoomControlOptions: {
              position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
            },
          }
        : null,
    [isLoaded, isDark, mapType],
  );

  let mapBody;
  if (loadError) {
    mapBody = (
      <div className="absolute inset-0 flex items-center justify-center">
        <EmptyState
          icon={TravelExploreRoundedIcon}
          title="Google Maps didn't load"
          description="Check your connection and reload the page."
        />
      </div>
    );
  } else if (!isLoaded) {
    mapBody = (
      <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm">
        <Spinner size={18} /> <span className="app-muted">Loading map…</span>
      </div>
    );
  } else {
    mapBody = (
      <>
        <GoogleMap
          mapContainerClassName="absolute inset-0"
          center={DEFAULT_CENTER}
          zoom={8}
          options={mapOptions}
          onLoad={(map) => {
            mapRef.current = map;
            if (circles.length) {
              const bounds = new window.google.maps.LatLngBounds();
              circles.forEach((c) => bounds.union(circleBounds(c, c.radius)));
              fitWithCap(map, bounds);
            }
          }}
          onUnmount={() => {
            mapRef.current = null;
          }}
          // Rank suggestions near whatever the map is showing (bias, not a limit).
          onIdle={() => {
            const bounds = mapRef.current && mapRef.current.getBounds();
            if (bounds && autocompleteRef.current)
              autocompleteRef.current.setBounds(bounds);
          }}
          onClick={(e) =>
            e.latLng && pickPoint({ lat: e.latLng.lat(), lng: e.latLng.lng() })
          }
        >
          {circles.map((c) => (
            <SavedCircle
              key={c.id}
              circle={c}
              selected={c.id === selectedId}
              circleRefs={circleRefs}
              onSelect={setSelectedId}
              onMapEdit={syncFromMap}
            />
          ))}

          {!selected && draftPoint && (
            <>
              <CircleF
                center={draftPoint}
                radius={radiusValid ? draftRadius : 0}
                options={{
                  strokeColor: draft.color,
                  strokeOpacity: 0.9,
                  strokeWeight: 2,
                  fillColor: draft.color,
                  fillOpacity: 0.08,
                  clickable: false,
                  zIndex: 5,
                }}
              />
              <MarkerF
                position={draftPoint}
                clickable={false}
                zIndex={900}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  fillOpacity: 0,
                  strokeColor: draft.color,
                  strokeWeight: 3,
                  scale: 7,
                }}
              />
            </>
          )}
        </GoogleMap>

        <form
          onSubmit={runSearch}
          className="absolute left-3 right-3 top-3 sm:right-auto sm:w-96"
          role="search"
        >
          <div className="app-glass flex h-11 items-center gap-2 rounded-full border pl-3.5 pr-1.5">
            <SearchRoundedIcon fontSize="small" className="app-muted" />
            <Autocomplete
              className="min-w-0 flex-1"
              fields={PLACE_FIELDS}
              onLoad={(ac) => {
                autocompleteRef.current = ac;
              }}
              onPlaceChanged={onPlaceChanged}
            >
              <input
                ref={searchInputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && suggestionHighlighted())
                    e.preventDefault();
                }}
                placeholder="Search an address or paste lat, lng"
                aria-label="Search an address or coordinates"
                className="w-full bg-transparent text-sm outline-none placeholder:text-[color:var(--app-placeholder)]"
              />
            </Autocomplete>
            {searching ? (
              <span className="px-2">
                <Spinner size={16} label="Searching" />
              </span>
            ) : (
              query.trim() && (
                <button
                  type="submit"
                  className="app-btn app-btn-primary app-btn-sm"
                >
                  Go
                </button>
              )
            )}
          </div>
          {searchError && (
            <p className="app-glass mt-2 rounded-xl border px-3 py-2 text-xs font-medium text-rose-600 dark:text-rose-400">
              {searchError}
            </p>
          )}
        </form>

        <div className="app-glass absolute right-3 top-[4.25rem] rounded-[0.9rem] border p-1 sm:top-3">
          <Segmented
            label="Map type"
            options={MAP_TYPES}
            value={mapType}
            onChange={setMapType}
          />
        </div>
      </>
    );
  }

  const addForm = (
    <>
      <PanelHeading
        title="Add a circle"
        hint="Click the map, search, or paste coordinates."
      />
      <form onSubmit={addCircle}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude" error={latError}>
              <input
                className={`${FIELD_CLASS} tabular-nums`}
                value={draft.lat}
                placeholder="41.753400"
                onPaste={onCoordPaste}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, lat: e.target.value }))
                }
              />
            </Field>
            <Field label="Longitude" error={lngError}>
              <input
                className={`${FIELD_CLASS} tabular-nums`}
                value={draft.lng}
                placeholder="-86.110800"
                onPaste={onCoordPaste}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, lng: e.target.value }))
                }
              />
            </Field>
          </div>
          <Field
            label="Radius / accuracy (m)"
            hint={radiusValid ? formatRadius(draftRadius) : ""}
            error={radiusError}
          >
            <input
              className={`${FIELD_CLASS} tabular-nums`}
              inputMode="decimal"
              value={draft.radius}
              onChange={(e) =>
                setDraft((d) => ({ ...d, radius: e.target.value }))
              }
            />
          </Field>
          <Field label="Label">
            <input
              className={FIELD_CLASS}
              value={draft.label}
              onChange={(e) =>
                setDraft((d) => ({ ...d, label: e.target.value }))
              }
            />
          </Field>
          <Field label="Color">
            <ColorPicker
              value={draft.color}
              onChange={(color) => setDraft((d) => ({ ...d, color }))}
            />
          </Field>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            type="button"
            className="app-btn app-btn-secondary app-btn-sm"
            disabled={locating}
            onClick={locateMe}
          >
            {locating ? (
              <Spinner size={14} label="Locating" />
            ) : (
              <MyLocationRoundedIcon sx={{ fontSize: 16 }} />
            )}
            My location
          </button>
          <button
            type="button"
            className="app-btn app-btn-secondary app-btn-sm"
            disabled={!draftPoint}
            onClick={() =>
              draftPoint && focusOn(draftPoint, radiusValid ? draftRadius : 0)
            }
          >
            <CenterFocusStrongRoundedIcon sx={{ fontSize: 16 }} />
            View
          </button>
          <button
            type="submit"
            className="app-btn app-btn-primary col-span-2"
            disabled={!draftPoint || !radiusValid}
          >
            <AddLocationAltRoundedIcon sx={{ fontSize: 18 }} />
            Add circle
          </button>
        </div>
        {locateError && (
          <p className="mt-2 text-xs font-medium text-rose-600 dark:text-rose-400">
            {locateError}
          </p>
        )}
      </form>
    </>
  );

  return (
    <PageShell size="full">
      <PageHeader
        icon={TravelExploreRoundedIcon}
        tone="teal"
        eyebrow="Tools"
        title="Geomapping"
        description="Plot GPS coordinates with their accuracy radius to see where a reading could actually be. Circles are saved in this browser only."
      />

      {/* One card, maps-app style: a fixed side panel (form on top, the
            circle list filling the rest and scrolling) beside a map that
            takes all remaining width. Phones stack the map above the panel. */}
      <Card
        padded={false}
        className="grid overflow-hidden lg:h-[calc(100vh-15rem)] lg:min-h-[600px] lg:grid-cols-[380px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]"
      >
        <aside className="app-divider order-2 flex min-h-0 flex-col border-t lg:order-1 lg:overflow-y-auto lg:border-r lg:border-t-0">
          <div className="app-divider shrink-0 border-b p-5">
            {selected ? (
              <CircleEditor
                key={selected.id}
                circle={selected}
                onChange={(patch) => updateCircle(selected.id, patch)}
                onDelete={() => removeCircle(selected.id)}
                onDone={() => setSelectedId(null)}
              />
            ) : (
              addForm
            )}
          </div>

          <section className="flex min-h-0 flex-1 flex-col lg:min-h-[13rem]">
            <div className="flex shrink-0 items-center justify-between gap-3 px-5 pb-2 pt-4">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold tracking-tight">
                  Circles
                </h2>
                {circles.length > 0 && (
                  <span className="app-chip tabular-nums">
                    {circles.length}
                  </span>
                )}
              </div>
              {circles.length > 0 &&
                (confirmClear ? (
                  <div className="flex items-center gap-1">
                    <span className="app-muted mr-1 text-xs">Remove all?</span>
                    <button
                      type="button"
                      className="app-btn app-btn-ghost app-btn-sm"
                      onClick={() => setConfirmClear(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="app-btn app-btn-danger app-btn-sm"
                      onClick={clearAll}
                    >
                      Clear all
                    </button>
                  </div>
                ) : (
                  <div className="-mr-2 flex items-center">
                    <button
                      type="button"
                      className="app-btn app-btn-ghost app-btn-sm"
                      onClick={fitAll}
                      disabled={!isLoaded}
                    >
                      <FitScreenRoundedIcon sx={{ fontSize: 16 }} />
                      Show all
                    </button>
                    <button
                      type="button"
                      className="app-btn app-btn-ghost app-btn-sm"
                      onClick={() => setConfirmClear(true)}
                    >
                      Clear all
                    </button>
                  </div>
                ))}
            </div>

            {circles.length === 0 ? (
              <EmptyState
                compact
                icon={RadarRoundedIcon}
                title="No circles yet"
                description="Circles you add show up here. Click one to edit, move, or delete it."
              />
            ) : (
              <ul className="relative max-h-80 overflow-y-auto pb-2 lg:max-h-none lg:min-h-0 lg:flex-1">
                {circles.map((c) => (
                  <CircleRow
                    key={c.id}
                    circle={c}
                    selected={c.id === selectedId}
                    onSelect={() => {
                      const opening = c.id !== selectedId;
                      setSelectedId(opening ? c.id : null);
                      if (opening) focusOn(c, c.radius);
                    }}
                    onFocus={() => focusOn(c, c.radius)}
                    onDelete={() => removeCircle(c.id)}
                  />
                ))}
              </ul>
            )}
          </section>
        </aside>

        <div className="relative order-1 h-[55vh] min-h-[320px] lg:order-2 lg:h-auto lg:min-h-0">
          {mapBody}
        </div>
      </Card>
    </PageShell>
  );
};

export default Geomapping;
